import React, { useMemo, useState } from 'react';
import { normalizeJobErrorCompare, resolveJobErrorDisplay } from '../../utils/errorUtils';
import './FailedJobErrorMessage.css';

export interface FailedJobErrorMessageProps {
  rawError?: string | null;
  fallbackMessage?: string;
  /** Extra backend details (pipeline task result, agent errors, etc.). */
  details?: string | null;
  /** Optional precomputed friendly text. */
  friendlyMessage?: string;
  className?: string;
  compact?: boolean;
}

const FailedJobErrorMessage: React.FC<FailedJobErrorMessageProps> = ({
  rawError,
  fallbackMessage = 'Job failed',
  details,
  friendlyMessage,
  className,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const display = useMemo(() => {
    if (friendlyMessage) {
      const raw = (rawError || '').trim() || fallbackMessage;
      const detailsText = (details || raw).trim();
      const hasDetails =
        detailsText.length > 0 &&
        normalizeJobErrorCompare(detailsText) !== normalizeJobErrorCompare(friendlyMessage);
      return {
        raw,
        friendly: friendlyMessage,
        hasDetails,
        details: detailsText || raw,
      };
    }
    const resolved = resolveJobErrorDisplay(rawError, fallbackMessage);
    if (details && details.trim()) {
      const hasDetails =
        normalizeJobErrorCompare(details) !== normalizeJobErrorCompare(resolved.friendly);
      return { ...resolved, details: details.trim(), hasDetails: resolved.hasDetails || hasDetails };
    }
    return resolved;
  }, [details, fallbackMessage, friendlyMessage, rawError]);

  const detailsText = (details || display.details || display.raw).trim();
  const showToggle =
    detailsText.length > 0 &&
    (display.hasDetails || normalizeJobErrorCompare(detailsText) !== normalizeJobErrorCompare(display.friendly));

  const detailItems = useMemo(() => {
    if (!detailsText) return [];
    const trimmed = detailsText.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) return [];
    return trimmed
      .split(/\n\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }, [detailsText]);

  const renderDetails = () => {
    if (detailItems.length > 1) {
      return (
        <ul className="failed-job-error__list">
          {detailItems.map((item, index) => (
            <li key={`${index}-${item.slice(0, 24)}`}>{item}</li>
          ))}
        </ul>
      );
    }
    return <pre className="failed-job-error__details">{detailsText}</pre>;
  };

  return (
    <div
      className={[
        'failed-job-error',
        compact ? 'failed-job-error--compact' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="failed-job-error__message">{display.friendly}</p>
      {showToggle ? (
        <>
          <button
            type="button"
            className="failed-job-error__toggle"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'View details'}
          </button>
          {expanded ? renderDetails() : null}
        </>
      ) : null}
    </div>
  );
};

export default FailedJobErrorMessage;
